import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import qs from 'query-string';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { desktopLogout } from 'containerApp';
import { OAuth, OAuthProvider } from './types/OAuthProvider';
import { useTokens } from './useTokens';
import { Tokens } from './types/Tokens';

const handleLogin = (provider: OAuthProvider) => () => {
  const uri = provider.loginRedirect();
  window.location.assign(uri);
};

const handleLogout = (reason?: any) => {
  window.updateTokens(undefined);
  sessionStorage.removeItem('ACTUALUSERURI');
  desktopLogout(reason);
  if (window.location.pathname !== '/') {
    window.location.assign('/');
  }
};

const handleOAuthCallback = (provider: OAuthProvider) => async () => {
  if (window.location.pathname !== provider.callbackPath) {
    return;
  }
  const query = qs.parse(window.location.search);

  if (!query || !query.code) {
    window.location.assign('/');
    return;
  }
  const newTokens = await provider.validate(
    query.code as string,
    query.state as string
  );
  window.updateTokens(newTokens);
  localStorage.setItem('new-login', 'true');
  window.location.assign('/');
};

const getTokenExpreTimeInMinutes = (tokens: Tokens | null): number => {
  if (tokens && tokens.expiresAt) {
    return (
      (new Date(tokens.expiresAt).getTime() - new Date().getTime()) /
      (60 * 1000)
    );
  }
  return -1;
};

export const updateTokenFromRefreshToken = async (
  refreshToken: string | undefined,
  provider: OAuthProvider
): Promise<'success' | 'failed' | string> => {
  if (refreshToken) {
    try {
      const refreshedTokens = await provider.refresh(refreshToken);
      Sentry.addBreadcrumb({
        data: {
          hasAccessToken: Boolean(refreshedTokens.accessToken),
          expiresAt: refreshedTokens.expiresAt,
          hasRefreshToken: Boolean(refreshedTokens.refreshToken)
        },
        level: Sentry.Severity.Info
      });
      window.updateTokens(refreshedTokens);
      return 'success';
    } catch (e) {
      Sentry.addBreadcrumb({
        data: {
          isOnline: window.navigator.onLine
        },
        level: Sentry.Severity.Info
      });
      if (axios.isAxiosError(e) && e.response) {
        return e.response.headers['x-execution-correlation-id'];
      }
      return 'failed';
    }
  }
  return 'failed';
};

export const autoIntervalRefreshToken = async (
  tokens: Tokens | null,
  provider: OAuthProvider,
  setHasValidToken: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  if (getTokenExpreTimeInMinutes(tokens) < 10) {
    if (
      (await updateTokenFromRefreshToken(tokens?.refreshToken, provider)) !=
      'success'
    ) {
      setHasValidToken(false);
    }
  }
};

export function useOAuth(provider: OAuthProvider): OAuth {
  const [isInitialized, setIsInitialized] = useState(false);
  const { tokenRef, hasValidToken, setHasValidToken } = useTokens();

  useEffect(() => {
    provider.initialize().then(() => {
      setIsInitialized(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleOAuthCallback(provider)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto refresh tokens
  useEffect(() => {
    const FIVE_MINUTE = 5 * 60 * 1000;
    const refresher = setInterval(async () => {
      await autoIntervalRefreshToken(
        tokenRef.current,
        provider,
        setHasValidToken
      );
    }, FIVE_MINUTE);

    return () => {
      clearInterval(refresher);
    };
  }, [tokenRef, provider, setHasValidToken]);

  const login = useCallback(() => {
    handleLogin(provider)();
  }, [provider]);

  const logout = useCallback((reason: any = null) => {
    handleLogout(reason);
  }, []);

  return {
    isInitialized,
    hasValidToken,
    tokenRef,
    login,
    logout
  };
}
